import { Clock } from "lucide-react";
import { useRef } from "react";

import type { ExtractedPrazo } from "@/api/client";

import { Button } from "../ui/button";

// "dias_uteis" sai como "dias", como no PrazoBadge: a unidade vem do regex e não
// decide a forma de contagem (CPC 219) — "15 dias" e "15 dias úteis" viram um chip.
const UNIDADE_EXTENSO: Record<string, [string, string]> = {
  dias: ["dia", "dias"],
  dias_uteis: ["dia", "dias"],
  horas: ["hora", "horas"],
  meses: ["mês", "meses"],
  anos: ["ano", "anos"],
};

interface Grupo {
  rotulo: string;
  posicoes: number[];
}

/** Agrupa as menções iguais ("6 meses" citado 19 vezes vira um chip só), na
 *  ordem da primeira aparição no texto. */
export function agruparPrazos(prazos: ExtractedPrazo[]): Grupo[] {
  const grupos = new Map<string, Grupo>();
  for (const p of [...prazos].sort((a, b) => a.posicao - b.posicao)) {
    const [um, varios] = UNIDADE_EXTENSO[p.unidade] ?? ["dia", "dias"];
    const rotulo = `${p.dias} ${p.dias === 1 ? um : varios}`;
    const g = grupos.get(rotulo);
    if (g) g.posicoes.push(p.posicao);
    else grupos.set(rotulo, { rotulo, posicoes: [p.posicao] });
  }
  return [...grupos.values()];
}

interface OutrosPrazosDoTextoProps {
  prazos: ExtractedPrazo[];
  /** Rola o texto até a menção e a destaca. */
  irPara: (posicao: number) => void;
}

/**
 * Os demais prazos que o texto cita, um chip por valor. O chip de um valor
 * citado várias vezes leva à próxima menção a cada toque.
 */
export function OutrosPrazosDoTexto({ prazos, irPara }: OutrosPrazosDoTextoProps) {
  // Próxima menção de cada grupo; ref porque não muda o que se vê.
  const proxima = useRef<Record<string, number>>({});
  const grupos = agruparPrazos(prazos);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {grupos.map((g) => {
        const vezes = g.posicoes.length;
        return (
          <Button
            key={g.rotulo}
            type="button"
            variant="outline"
            size="sm"
            className="h-auto min-h-[44px] gap-1 rounded-full px-2.5 py-0.5 text-xs font-normal text-muted-foreground sm:min-h-0"
            aria-label={
              vezes > 1
                ? `Ver no texto: ${g.rotulo}, citado ${vezes} vezes`
                : `Ver no texto: ${g.rotulo}`
            }
            onClick={() => {
              const i = proxima.current[g.rotulo] ?? 0;
              irPara(g.posicoes[i % vezes]);
              proxima.current[g.rotulo] = (i + 1) % vezes;
            }}
          >
            <Clock className="h-3 w-3" aria-hidden />
            {g.rotulo}
            {vezes > 1 && (
              <span className="tabular-nums text-muted-foreground/70">×{vezes}</span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
